import * as React from "react";
import { Grid, Form, Button, Header, Icon, Message, Checkbox } from "semantic-ui-react";
import { Field, Formik } from "formik";
import { Profile, BaseCoach, CoachStatuses, UserGroups } from "@/interfaces";
import { CoachService, SocialCoachSessionService, UsersService } from "@/services";
import { phoneNumberMask } from "@/utils";

import { validationSchema } from "./validations";

import "./styles.scss";
import MaskedInput from "react-text-mask";
import { SCAccount } from "@/providers";

interface OwnProps {
  profile: Profile;
  user: SCAccount;
  coach: BaseCoach;
  handleUpdateSuccess?: () => void;
}

const CoachInformationFC: React.FC<OwnProps> = ({ profile, user, coach, handleUpdateSuccess }) => {
  const [coachStatus, setCoachStatus] = React.useState<CoachStatuses>(coach.coachStatus!!);
  const [confirmed, setConfirmed] = React.useState<boolean>(user.status !== "PENDING");
  const [isDemo, setIsDemo] = React.useState<boolean>(user.isDemo || false);
  const [isEditor, setIsEditor] = React.useState<boolean>(false);
  const [userGroup, setUserGroups] = React.useState<any[]>(user!!.groups || []);

  const updateCoachStatusRequest = async (status: CoachStatuses) => {
    await CoachService.updateCoachStatus(coach.id, status);
    setCoachStatus(status);
  };

  React.useEffect(() => {
    const editor = userGroup.some(g => g === UserGroups.EDITOR);
    setIsEditor(editor);
  }, [userGroup]);

  const authenticateUser = (): void => {
    if (user.status === "PENDING") {
      UsersService.adminAuthenticateCoachEmail(user.userId).then(() => {
        setConfirmed(true);
      });
    }
  };

  const updateUserGroup = (groups: UserGroups[]): void => {
    const update = {
      ...user,
      groups,
    };

    UsersService.updateUserGroup(update).catch(console.error);
  };

  return (
    <Grid columns="1" className={"coachInfoForm"}>
      <Grid.Row>
        <Grid.Column>
          <Formik
            initialValues={{
              firstName: profile.firstName,
              lastName: profile.lastName,
              title: coach.title,
              company: coach.companyName,
              phone: profile.phone,
              email: user.email,
              status: coach.coachStatus,
              type: coach.type || "",
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setErrors }) => {
              const updatedUser = {
                coach: {
                  ...coach,
                  title: values.title,
                  companyName: values.company,
                },
                profile: {
                  ...profile,
                  firstName: values.firstName,
                  lastName: values.lastName,
                  phone: values.phone,
                },
                user: {
                  ...user,
                  email: values.email,
                  username: values.email,
                },
              };

              SocialCoachSessionService.updateCoach(coach.id, updatedUser, user.userId).then(response => {
                if (response.user && (response.user as { code?: string }).code === "account_http_409") {
                  setErrors({
                    email: "Username already taken",
                  });
                } else {
                  if (handleUpdateSuccess) {
                    handleUpdateSuccess();
                  }
                }
              });
            }}
          >
            {({ values, errors, handleChange, handleSubmit }) => {
              const errorList = Object.values(errors) as string[];
              return (
                <Form data-elm-id="coach-information-form" onSubmit={handleSubmit} error={errorList.length > 0}>
                  <Grid className={"coachSocialNetwork"}>
                    <Message error list={errorList} />
                  </Grid>
                  <Form.Group widths="equal">
                    <Form.Input
                      value={values.firstName}
                      data-elm-id="firstname-input"
                      type="text"
                      size="mini"
                      id="firstName"
                      name="firstName"
                      onChange={handleChange}
                      fluid
                      label="First name"
                      placeholder="First name"
                    />
                    <Form.Input
                      value={values.lastName}
                      onChange={handleChange}
                      data-elm-id="lastname-input"
                      type="text"
                      size="mini"
                      id="lastName"
                      name="lastName"
                      fluid
                      label="Last name"
                      placeholder="Last name"
                    />
                  </Form.Group>
                  <Form.Group widths="equal">
                    <Form.Input
                      value={values.title}
                      onChange={handleChange}
                      data-elm-id="title-input"
                      type="text"
                      size="mini"
                      id="title"
                      name="title"
                      fluid
                      label="Title"
                      placeholder="Title"
                    />
                    <Form.Input
                      value={values.company}
                      onChange={handleChange}
                      data-elm-id="company-input"
                      type="text"
                      size="mini"
                      id="company"
                      name="company"
                      fluid
                      label="Company"
                      placeholder="Company"
                    />
                  </Form.Group>
                  <Form.Group widths="equal">
                    <div className="field">
                      <label>Phone</label>
                      <Field name="phoneNumber" data-elm-id={"coach-infor-phone-number"}>
                        {() => (
                          <MaskedInput
                            value={values.phone}
                            mask={phoneNumberMask}
                            id="phone"
                            placeholder={"Phone"}
                            type="text"
                            onChange={handleChange}
                            data-elm-id="phone-input"
                          />
                        )}
                      </Field>
                    </div>
                    <Form.Input
                      value={values.email}
                      onChange={handleChange}
                      data-elm-id="email-input"
                      type="text"
                      size="mini"
                      id="email"
                      name="email"
                      fluid
                      label="Email"
                      placeholder="Email"
                    />
                    <Form.Input
                      value={coachStatus}
                      onChange={handleChange}
                      data-elm-id="status-input"
                      type="text"
                      size="mini"
                      id="status"
                      disabled
                      name="status"
                      fluid
                      label="Status"
                      placeholder="Status"
                    />
                  </Form.Group>
                  <Grid className={"coachSocialNetwork"}>
                    <Grid.Row>
                      <Grid.Column>
                        {" "}
                        <Header as="h4">
                          Authenticate Email:
                          <span className="buttons-link" onClick={authenticateUser}>
                            {"  " + user.email} {confirmed && <Icon color="green" name="checkmark" size="small" />}
                          </span>
                        </Header>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>

                  <Button.Group floated={"right"}>
                    <Button.Group floated={"left"}>
                      <Checkbox
                        className={"mediumMarginTop mediumMarginRight"}
                        checked={isDemo}
                        label="Is a demo/test account"
                        onChange={(_, e) => {
                          setIsDemo(e.checked || false);
                          UsersService.setDemoAccount(user.userId, e.checked || false).then(() => {
                            if (handleUpdateSuccess) {
                              handleUpdateSuccess();
                            }
                          });
                        }}
                      />
                    </Button.Group>

                    <Button.Group floated={"left"}>
                      <Checkbox
                        className={"mediumMarginTop mediumMarginRight"}
                        checked={isEditor}
                        label="Is an Editor"
                        onChange={(_, e) => {
                          const groups = e.checked ? [UserGroups.EDITOR] : [UserGroups.COACH];
                          setUserGroups(() => groups);
                          updateUserGroup(groups);
                        }}
                      />
                    </Button.Group>

                    <Button.Group floated={"right"}>
                      <Button
                        data-elm-id={`coachInfoActiveOrApproveBtn`}
                        compact
                        disabled={coachStatus === CoachStatuses.ACTIVE}
                        color="green"
                        type="button"
                        onClick={async () => {
                          updateCoachStatusRequest(CoachStatuses.ACTIVE).catch(console.error);
                        }}
                      >
                        {coachStatus === CoachStatuses.PENDING_FOR_APPROVAL ? "Approve Coach" : "Active Coach"}
                      </Button>
                      <Button.Or />
                      <Button
                        data-elm-id={`coachInfoDisableBtn`}
                        compact
                        disabled={coachStatus !== CoachStatuses.ACTIVE}
                        color="red"
                        type="button"
                        onClick={() => {
                          if (
                            window.confirm(
                              "Are you sure you want to disable this coach? All players will be de-activated, all social connections deleted."
                            )
                          )
                            updateCoachStatusRequest(CoachStatuses.INACTIVE).catch(console.error);
                        }}
                      >
                        Disable Coach
                      </Button>
                    </Button.Group>

                    <Form.Button
                      data-elm-id={`coachInfoSaveBtn`}
                      compact
                      className={"leftPadding"}
                      disabled={errorList.length > 0}
                      type="submit"
                    >
                      Save Coach
                    </Form.Button>
                  </Button.Group>
                </Form>
              );
            }}
          </Formik>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export const CoachInformation = CoachInformationFC;
